import React, { useEffect, useLayoutEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';

import { inputDeviceId, outputDeviceId } from '@magoosh/organisms/audio/audio_devices';
import { loadVolume, persistVolume } from '@magoosh/organisms/audio/volume';

import AudioStimulus from '../audio_stimulus';
import VideoStimulus from '../video_stimulus';

import beepSrc from './assets/beep.mp3';

interface Props {
  audioRecorder: {
    autoStart: boolean;
  };
  compatibilityMode: boolean;
  isAnswered: boolean;
  stimulus: {
    direction: string;
    posterSrc?: string;
    referenceImageSrc: string;
    src: string;
    type: 'audio' | 'video';
  };
}

// The audio recorder is rendered separately by Rails as an AnswerAudioInput. We interact with it
// a few ways in this component:
//   * We hide it by default, then show it in a useEffect. This allows us to change some of the
//     default states/UI if needed without it flashing in the UI.
//   * We manually change the recorder button text if the audio recorder will auto start
//   * We send events that the recorder listens to to start recording and to reset
const applySettingsToAnswerAudio = (volume: number) => {
  const answerAudio = document.querySelector('#answer_parts_val_input audio') as HTMLAudioElement | null;
  if (!answerAudio) return;

  answerAudio.volume = volume;
  if ('setSinkId' in answerAudio) {
    (answerAudio as any).setSinkId(outputDeviceId()).catch(() => {});
  }
};

export default (props: Props) => {
  const [compatibilityMode, setCompatibilityMode] = useState<boolean>(props.compatibilityMode);
  const [volume, setVolume] = useState<number>(1);

  useEffect(() => {
    const savedVolume = loadVolume();
    setVolume(savedVolume);
    applySettingsToAnswerAudio(savedVolume);
  }, []);

  useEffect(() => {
    applySettingsToAnswerAudio(volume);
  }, [volume]);

  useLayoutEffect(() => {
    const recorder = document.querySelector('.audio-recorder') as HTMLElement | null;

    if (recorder) {
      if (!compatibilityMode) prepareRecorderForAutoPlayback();
      recorder.style.display = 'block';
    }
  }, [compatibilityMode]);

  const handlePlaybackFinished = () => {
    if (props.audioRecorder.autoStart && !compatibilityMode) {
      // The audio recorder listens for this event
      document.dispatchEvent(
        new CustomEvent(
          'startRecording',
          { detail: { deviceId: inputDeviceId(), suppressBeep: true } }
        )
      );

      // The audio recorder has a slight delay before recording actually starts so that bluetooth
      // input devices can initialize. The delay here results in the beep happening just
      // as recording starts but without the beep being included in the recording.
      if (!isMobile) setTimeout(() => playBeep(outputDeviceId()), 1000);
    }
  };

  const handleResetAudioRecorder = (updatedCompatibilityMode) => {
    // The audio recorder listens for this event
    const resetEvent = new CustomEvent(
      'resetRecordingState',
      { detail: { state: updatedCompatibilityMode ? 'ready' : 'ready_auto' } }
    );
    document.dispatchEvent(resetEvent);

    if (!updatedCompatibilityMode) prepareRecorderForAutoPlayback();

    // Re-apply output device in case it changed in settings
    applySettingsToAnswerAudio(volume);
  };

  const playBeep = (sinkId?: string) => {
    return new Promise((resolve, reject) => {
      const audio = new Audio(beepSrc);

      if (sinkId && 'setSinkId' in audio) {
        (audio as any).setSinkId(sinkId).catch(() => {});
        audio.play().catch(() => {});
      } else {
        audio.play().catch(() => {});
      }
    });
  };

  const prepareRecorderForAutoPlayback = () => {
    const recorder = document.querySelector('.audio-recorder') as HTMLElement | null;

    if (recorder && props.audioRecorder.autoStart && !compatibilityMode) {
      // The recorder is loaded through Rails and starts in a ready state. Move the UI to match
      // the ready_auto state.
      recorder.querySelector('.audio-recorder-instructions').innerHTML = 'Recording will begin automatically';
      recorder.querySelector('.audio-recorder-btn span').innerHTML = 'Waiting';
      recorder.querySelector('.audio-recorder-btn').classList.remove('btn-primary');
      recorder.querySelector('.audio-recorder-btn').classList.add('btn-default');
      recorder.querySelector('.audio-recorder-btn').classList.add('disabled');
    }
  };

  const renderStimulus = () => {
    if (props.stimulus.type === 'audio') {
      return (
        <AudioStimulus
          compatibilityMode={compatibilityMode}
          isAnswered={props.isAnswered}
          onChangeCompatibilityMode={setCompatibilityMode}
          onChangeVolume={setVolume}
          onFinishPlayback={handlePlaybackFinished}
          onResetPlayback={handleResetAudioRecorder}
          playButtonText="Play Audio and Record Response"
          {...props.stimulus}
        />
      );
    } else if (props.stimulus.type === 'video') {
      return (
        <VideoStimulus
          compatibilityMode={compatibilityMode}
          isAnswered={props.isAnswered}
          onChangeCompatibilityMode={setCompatibilityMode}
          onChangeVolume={setVolume}
          onFinishPlayback={handlePlaybackFinished}
          onResetPlayback={handleResetAudioRecorder}
          playButtonText="Play Video and Record Response"
          {...props.stimulus}
        />
      );
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: '.audio-recorder { display: none }' }} />
      {renderStimulus()}
    </>
  );
};
